// ****************** NOTE ******************
// Type changes are following flow below:
// .
// .
// GET = api: TPermissionFromApi --> class: TUserAccessControlModel --> component: TAccessAndPermissionInput
// PUT = reverse it!
//
// Why three types of data? because we have three places to use:
// 1.

import {
  Base,
  FetchingServices,
  type FetchingResponse,
} from '@/composables/base';
import { reactive } from 'vue';
import { useCookie } from '~/composables/useCookie';
import {
  EGlobalStoreEnums,
  type EGlobalFormType,
} from '~/composables/useGlobal';
import { useToastStore } from '~/stores/toast';
import { useSettingsUser } from '../user';
import {
  AccessEntries,
  type TAccess,
  type TAccessGroup,
  type TCustomPermission,
  type TNoPolicyPermission,
  type TPermission,
  type TPolicy,
  type TVisibility,
} from './AccessEntries';
import {
  EXCEPTION_SUBMENU_BASED_ON_PERMISSION,
  useSettingsRAPRoleService,
} from './role-service';

//  |
//  |   Role Types
// @@@
//  @

type TVisibilityFromApi = {
  [key in TVisibility]: boolean | string;
};

type TPermissionFromApi = {
  permissions: {
    [accessKey in TAccess]: {
      [permKey in TNoPolicyPermission]: boolean;
    } & {
      [permKey in Exclude<
        TPermission | TCustomPermission,
        TNoPolicyPermission
      >]: Partial<Record<TPolicy, boolean>>;
    };
  };
  description: string;
  role: string;
};
type TUpdateApiBody = {
  add_permissions?: TPermissionFromApi['permissions'];
  delete_permissions?: TPermissionFromApi['permissions'];
  role: string;
};
export type TRecordAccessApi = {
  [key in TPermission | TCustomPermission]: boolean;
};

export type TRoleModel = {
  default: boolean;
  role: string;
  description: string;
};

export type TUserAccessControlModel = {
  role: string;
  description: string;
  list: {
    access: TAccess;
    permission: TPermission | TCustomPermission;
    policy: TPolicy | null;
  }[];
};

export type TAccessControlList = {};
export type TInfoFormData = { role: string; description: string };

//  |
//  |   Filter Criterias type
// @@@
//  @
type TFilterCriterias = {
  page_number: number;
  page_size: number;
};

//  |
//  |   Component type
// @@@
//  @
export type TAccessAndPermissionInput = {
  role: string;
  name: string;
  key: TAccessGroup;
  access: {
    name: string;
    key: TAccess;
    accessPanelIsOpen: number | null;
    hasOwnerPaid: boolean;
    selectAll: string | boolean;
    permissions: {
      name: string;
      key: TPermission | TCustomPermission;
      isChecked: TPermission | TCustomPermission | false;
      policy: TPolicy | null;
    }[];
  }[];
};

export type TPolicyPriorityInput = {
  title: string;
  value: TPolicy;
  priority: number;
  props:
    | {
        disabled: boolean;
        baseColor: string;
      }
    | undefined;
};

//  |
//  |   Function parameters type
// @@@
//  @
type TGetRecordAccessApiParams = {
  service: FetchingServices;
  url: string;
  formType?: EGlobalFormType;
  body?: {
    id: string | string[] | number;
  };
  id?: string | number;
};

//  |
//  |   Interface and Class
// @@@
//  @
interface IRolesAndPermissions {
  userAccess: TUserAccessControlModel | undefined;
  visibilityList: TVisibilityFromApi | undefined;

  // filterCriterias: TFilterCriterias;

  // private
  // updateSidebarMenuVisibility(): void;
  // generateUpsertBody(formData: TUserAccessControlModel): TPermissionFromApi

  // api
  getVisibilityListApi(): Promise<boolean>;
  getRecordAccessApi(
    apiObject: TGetRecordAccessApiParams
  ): Promise<Partial<TRecordAccessApi> | null>;

  // setters & getters
  setVisibilityList(list: TVisibilityFromApi): void;
  setUserAccess(accesssObj: TUserAccessControlModel): void;

  // public
  initUserAccess(): void;
  checkIfOwnerPaidAccess(access: TAccess): boolean;
}
export class RolesAndPermissions extends Base implements IRolesAndPermissions {
  userAccess: TUserAccessControlModel | undefined = undefined;
  visibilityList: TVisibilityFromApi | undefined = undefined;

  // filterCriterias: TFilterCriterias = {
  //   page_number: 1,
  //   page_size: 10,
  // };

  private async updateSidebarMenuVisibility(
    userRoleList: TUserAccessControlModel['list']
  ) {
    // If user is OWNER
    if (useSettingsUser.isOwner) {
      // manage the menu with visibility like what was implemented before
      return;
    }

    // If user have ROLE reset the permission
    // based on the users role
    // NOTE: This file must be imported dynamically because it uses a Pinia store.
    // If imported statically, it runs before Pinia is initialized in the app,
    // causing a "Pinia not initialized" error.
    const sidebarData =
      await import('~/components/lc/Full/vertical-sidebar/sidebarItem');

    // overiding the old permissions by the new ones of the Sidebar Menu
    sidebarData.default.forEach((menuItem) => {
      menuItem.children?.forEach((subMenuItem) => {
        const hasAccess = userRoleList.some((acc) => {
          if (!subMenuItem.to) return false;

          const hasAcc = AccessEntries.get(subMenuItem.to)?.user.includes(
            acc.access
          );

          // Check if the permission is based on the exception permissions
          const exception = EXCEPTION_SUBMENU_BASED_ON_PERMISSION?.[acc.access];

          if (!exception) return hasAcc;
          else
            return exception.some((item) => {
              return (
                item.link === subMenuItem.to &&
                item.permissions.includes(acc.permission)
              );
            });
        });
        // apply role permissions
        if (subMenuItem.permission) {
          subMenuItem.permission = hasAccess;
        }
        // apply public pages
        const accIsPublic =
          subMenuItem.to &&
          AccessEntries.get(subMenuItem.to)?.user.includes('open_to_all');
        if (accIsPublic) subMenuItem.permission = true;
      });
    });

    // caculate which AccessGroup should be shown or not
    sidebarData.default.forEach((menuItem) => {
      const activeChildCount = menuItem.children?.reduce((acc, child) => {
        if (child.permission) return acc + 1;
        else return acc;
      }, 0);
      if (activeChildCount) menuItem.permission = activeChildCount > 0;
      else menuItem.permission = false;
    });

    // overiding the old permissions by the new ones of the Sidebar Menu
  }
  private filterRoleBasedOnVisibility(
    userRole: TUserAccessControlModel
  ): TUserAccessControlModel {
    if (!this.visibilityList) {
      console.error('visibility list not found');
      return { ...userRole, list: [] };
    }
    // remove roles if there is not in visibility
    const filteredRole: TUserAccessControlModel = { ...userRole, list: [] };
    const accessEntry = [...AccessEntries.entries()];
    userRole.list.forEach((item) => {
      const userAccessTuple = accessEntry.find((key) => {
        return key[1].user.includes(item.access);
      });

      if (userAccessTuple) {
        userAccessTuple[1].owner.forEach((ownerAccess) => {
          if (this.visibilityList && this.visibilityList[ownerAccess]) {
            filteredRole.list.push(item);
          }
        });
      }
    });
    return filteredRole;
  }

  async getVisibilityListApi(): Promise<boolean> {
    try {
      const res = await this.get<TVisibilityFromApi>(
        FetchingServices.ipbx,
        'visibility/menu'
      );
      if (res.data) {
        this.setVisibilityList(res.data);
        return true;
      }
      return false;
    } catch (err) {
      // handel error
      console.log('get visibility list error');
      return false;
      // TODO: refetch until you get the
    }
  }
  async getRecordAccessApi(
    apiObject: TGetRecordAccessApiParams
  ): Promise<Partial<TRecordAccessApi> | null> {
    try {
      let res: FetchingResponse<Partial<TRecordAccessApi>> | null = null;
      // for ci record permissions
      if (apiObject.formType) {
        res = await this.get<Partial<TRecordAccessApi>>(
          apiObject.service,
          `${apiObject.url}/${apiObject.formType}/${apiObject.id}`
        );
      }
      // for record permissions with id in params
      else if (apiObject.id) {
        res = await this.get<Partial<TRecordAccessApi>>(
          apiObject.service,
          apiObject.url + '/' + apiObject.id
        );
      }
      // for record permissions with id in body
      else {
        res = await this.post<Partial<TRecordAccessApi>>(
          apiObject.service,
          apiObject.url,
          apiObject.body
        );
      }
      if (res.data) return res.data;
      return null;
    } catch (err) {
      console.log('get record access error');
      return null;
    }
  }

  // Setters
  setUserAccess(accessObj: TUserAccessControlModel) {
    this.userAccess = { ...accessObj };
  }
  setVisibilityList(list: TVisibilityFromApi) {
    this.visibilityList = { ...list };
  }

  // Publics
  public async initUserAccess() {
    // Scenarioes:
    // I. state is full
    // II. page refreshed localStore is full
    // III. no state no localStore

    try {
      // I: Check if there state is full (no client page refresh happened yet)
      if (this.userAccess?.role) return;

      // II: Check if there is in the local storage
      if (process.env.NODE_ENV === 'production') {
        const localStoreData = this.store<TUserAccessControlModel>(
          EGlobalStoreEnums.RAP_USER_ROLES
        );
        if (localStoreData.role) {
          this.setUserAccess(localStoreData);
          this.userAccess &&
            this.updateSidebarMenuVisibility(this.userAccess.list);
          return;
        }
      }

      // III: THERE IS NO DATA IN THE STORE SO:
      // 1. get user id
      if (useSettingsUser.isOwner && this.visibilityList) return;
      if (useSettingsUser.isOwner) {
        const res = await this.getVisibilityListApi();
        // owner has all access but visibilities
        // so no need to manage the roles
        // it will apply when creating the sidebar menu
        if (!res) {
          // remove token from local storage
          throw new Error('visibility list not found');
        }
        return;
      }

      await this.getVisibilityListApi();

      // user is not OWNER
      const userId = useSettingsUser.getUserId();
      if (!userId) throw new Error('user id not found in visibility');

      // 1.5 check if the user id is in the navalist

      // 2. get subuser list
      await useSettingsUser.getUsers();

      // 3. find users
      let user = useSettingsUser.userList.find((u) => u.uuid === userId);
      if (!user) {
        // TODO: maybe this is the id scenario and must searched by number or call the new api to get actual currect id
        const userMainId = await useSettingsUser.getUserMainUuidApi();
        if (userMainId) {
          // update the store
          useSettingsUser.updateUser({
            ...useSettingsUser.user,
            sub: userMainId,
          });
          // find the user
          user = useSettingsUser.userList.find((u) => u.uuid === userMainId);
        }
        if (!user) throw new Error('user not found');
      }
      if (!user?.role) throw new Error('user role not found in user list');

      // 4. get permissions by role name
      const roleRes = await useSettingsRAPRoleService.apiGetRole(
        user.role,
        true
      );
      if (!roleRes) throw new Error("didn't find the role");

      const convertedRoles =
        useSettingsRAPRoleService.normalizeUserPermissionApiToClassType(
          roleRes
        );
      const filteredData = this.filterRoleBasedOnVisibility(convertedRoles);

      // 5. set Role to the local storage - state - apply on sidebar
      this.store(EGlobalStoreEnums.RAP_USER_ROLES, filteredData);
      this.setUserAccess(filteredData);
      if (!this.userAccess) throw new Error("roles didn't set currectly");
      if (this.userAccess.role)
        this.updateSidebarMenuVisibility(this.userAccess.list);

      // End of the scenarioes
    } catch (err) {
      // if any problem happens, remove the token from the local storage
      const accessToken = useCookie('accessToken');
      const adminNumber = useCookie('adminNumber');

      accessToken.value = null;
      adminNumber.value = null;
      throw new Error("roles didn't set currectly: " + err);
    }
  }
  public checkIfOwnerPaidAccess(access: TAccess): boolean {
    // I. There is no OwnerAccess
    if (!this.visibilityList) {
      return false;
    }

    // II. Owner Visibility is available
    const vis = this.visibilityList;
    if (!vis) return false; // just for type check

    // 1. find owner access key from access
    const recordTuple = Array.from(AccessEntries.entries()).find(([_, v]) =>
      v.user.includes(access)
    );
    const ownerList = recordTuple?.[1].owner; // Get the second el of the record tuple { user, owner}
    if (!ownerList) return false;

    // 2. figure out owner has that key with true value or not
    return ownerList.some((item) => vis[item] === true);
  }
  public getRecordAccessNotification(
    recordAccess: Partial<TRecordAccessApi> | null,
    permission: TPermission | TCustomPermission,
    noModal?: boolean
  ): boolean {
    if (recordAccess && recordAccess[permission]) return true;

    if (!noModal)
      useToastStore().showAlert({
        title: 'unauthorized',
        text: 'youDontHavePermissionToDoThisAction',
        confirmButtonText: 'gotIt',
        showConfirmButton: true,
        type: 'error',
      });
    return false;
  }
}

export const useSettingsRAP = reactive(new RolesAndPermissions());
